/**
 * Tab 4: Risk Matrix Configuration
 * 
 * Features:
 * - Likelihood Degree Scale table (fixed 5 rows, inline editable descriptions)
 * - Risk Impact Severity Scale table (fixed 5 rows, inline editable descriptions)
 * - Visual risk matrix (editable result cells)
 * - Used to set Control Risk Ranking in Tab 2
 * 
 * Global configuration that applies to all risk assessments across the application.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { message } from 'antd';
import styled from 'styled-components';
import { DownOutlined } from '@ant-design/icons';
import ExportButton from '../../../../components/common/ExportButton';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from '@syncfusion/ej2-react-grids';
import { RootState, AppDispatch } from '../../../../store';
import {
  setLikelihoodRubricItems,
  setSeverityRubricItems,
} from '../../../../store/slices/riskAssessmentSlice';
import {
  getLikelihoodRubricItems,
  getSeverityRubricItems,
  updateRubricItem,
  parseRubricItems,
  type RubricItem,
} from '../../../../services/riskAssessmentService';
import RiskMatrixTable from './RiskMatrixTable';
import ExportPanel, { ExportColumnConfig } from '../../../../components/common/ExportPanel';

// ========== TYPES ==========

// Use RubricItem from service instead of separate types

// ========== STYLED COMPONENTS ==========

const TabContent = styled.div`
  min-height: 400px;
`;

const Section = styled.div<{ $bgColor: string }>`
  margin-bottom: 24px;
  background: ${props => props.$bgColor};
  padding: 16px 2px 2px 2px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  margin-left: 16px;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const CollapseIcon = styled.div`
  display: flex;
  align-items: center;
  color: #6B7280;
  transition: transform 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SectionTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  color: #1F2937;
  flex: 1;
`;

const SectionActions = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;
`;

const GridWrapper = styled.div`
  background: white;
  border-radius: 6px;
  overflow: hidden;

  .e-grid {
    border: none;
  }

  .e-grid .e-headercell {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
  }

  .e-grid .e-headercell:last-child {
    border-right: none;
  }

  .e-grid .e-rowcell {
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
    padding: 6px 8px;
    font-size: 12px;
    color: #1F2937;
  }

  .e-grid .e-rowcell:last-child {
    border-right: none;
  }

  .e-grid .e-emptyrow {
    display: none;
  }

  .e-grid .e-row.e-active .e-rowcell,
  .e-grid .e-row.e-selectionbackground .e-rowcell,
  .e-grid .e-row.e-active,
  .e-grid .e-row.e-selectionbackground {
    background: transparent !important;
  }
`;

const IndexCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
`;

const VisualTableSection = styled.div`
  margin-bottom: 24px;
  background: #EDE9FE;
  padding: 16px 2px 2px 2px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const RiskMatrixContainer = styled.div`
  background: white;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const EditableDescriptionCell = styled.div`
  position: relative;
  min-height: 24px;
  padding: 4px 8px;
  cursor: text;
  
  &:hover {
    background: #F9FAFB;
    border-radius: 4px;
  }
`;

const DescriptionText = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  color: #1F2937;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const DescriptionTextarea = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 8px;
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  color: #1F2937;
  line-height: 1.5;
  border: 1px solid #2563EB;
  border-radius: 4px;
  resize: vertical;
  outline: none;
  
  &:focus {
    border-color: #2563EB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  text-align: center;
`;

const EmptyStateText = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  color: #6B7280;
  margin-bottom: 16px;
`;

const ReloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #2563EB;
  color: white;
  border: none;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #1D4ED8;
  }

  &:disabled {
    background: #9CA3AF;
    cursor: not-allowed;
  }
`;

// ========== COMPONENT ==========

const Tab4RiskMatrixConfiguration: React.FC = () => {
  // Get selectedReport from Redux to access annualReportId
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const annualReportId = selectedReport?.annualReportId;
  const dispatch = useDispatch<AppDispatch>();
  
  // Get rubric items from Redux
  const likelihoodRubricItems = useSelector((state: RootState) => state.riskAssessment.likelihoodRubricItems);
  const severityRubricItems = useSelector((state: RootState) => state.riskAssessment.severityRubricItems);
  // console.log('likelihoodRubricItems', likelihoodRubricItems);
  // console.log('severityRubricItems', severityRubricItems);

  // Local state for editing
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<string | null>(null);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Refs to prevent duplicate fetches and track assessmentId changes
  const hasFetchedRef = useRef(false);
  const currentAnnualReportIdRef = useRef<string | undefined>(undefined);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // Export panel states
  const [exportLikelihoodPanelOpen, setExportLikelihoodPanelOpen] = useState(false);
  const [exportSeverityPanelOpen, setExportSeverityPanelOpen] = useState(false);

  // Export columns configuration (same for both tables)
  const exportColumns: ExportColumnConfig[] = useMemo(() => [
    { key: 'index', title: '#', width: 10 },
    { key: 'name', title: 'Name', width: 25 },
    { key: 'description', title: 'Description', width: 65 },
  ], []);

  // Export data for Likelihood
  const likelihoodExportData = useMemo(() => 
    likelihoodRubricItems.map((item, idx) => ({
      index: String(idx + 1).padStart(2, '0'),
      name: item.name,
      description: item.description || '',
    }))
  , [likelihoodRubricItems]);

  // Export data for Severity
  const severityExportData = useMemo(() => 
    severityRubricItems.map((item, idx) => ({
      index: String(idx + 1).padStart(2, '0'),
      name: item.name,
      description: item.description || '',
    }))
  , [severityRubricItems]);

  // Check if data is ready (both arrays have data)
  const isDataReady = likelihoodRubricItems.length > 0 && severityRubricItems.length > 0;

  // Fetch both rubric items in parallel
  const fetchRubricItems = useCallback(async () => {
    if (!annualReportId) return;

    setIsLoading(true);
    setHasError(false);
    
    try {
      // console.log('🔄 Fetching rubric items...');
      
      // Fetch both in parallel
      const [likelihoodResponse, severityResponse] = await Promise.all([
        getLikelihoodRubricItems(annualReportId, { skip: 0, take: 5 }),
        getSeverityRubricItems(annualReportId, { skip: 0, take: 5 }),
      ]);

      const parsedLikelihood = parseRubricItems(likelihoodResponse);
      const parsedSeverity = parseRubricItems(severityResponse);
      
      // Dispatch both to Redux at once
      dispatch(setLikelihoodRubricItems(parsedLikelihood));
      dispatch(setSeverityRubricItems(parsedSeverity));
      
      // console.log(`✅ Rubric items loaded: ${parsedLikelihood.length} likelihood, ${parsedSeverity.length} severity`);
    } catch (error: any) {
      console.error('❌ Failed to fetch rubric items:', error);
      message.error(error?.message || 'Failed to load rubric items');
      setHasError(true);
      hasFetchedRef.current = false; // Allow retry
    } finally {
      setIsLoading(false);
    }
  }, [annualReportId, dispatch]);

  // Fetch data on mount and when annualReportId changes
  useEffect(() => {
    if (!annualReportId) return;

    // Check if annualReportId changed
    const isNewAnnualReport = currentAnnualReportIdRef.current !== annualReportId;
    
    if (isNewAnnualReport) {
      // console.log(`🆕 Rubric Items: Annual Report ID changed: ${currentAnnualReportIdRef.current} → ${annualReportId}`);
      hasFetchedRef.current = false;
      currentAnnualReportIdRef.current = annualReportId;
    }

    // Check if data is already in Redux for this annual report
    if (isDataReady && hasFetchedRef.current) {
      // console.log('✅ Rubric items already in Redux');
      return;
    }

    // Skip if already fetched for this annual report
    if (hasFetchedRef.current) {
      // console.log('⏭️ Skipping duplicate rubric items fetch for:', annualReportId);
      return;
    }

    // Mark as fetched before fetching to prevent duplicates
    hasFetchedRef.current = true;
    fetchRubricItems();
  }, [annualReportId, isDataReady, fetchRubricItems]);

  // Handle description edit start - set initial value once via ref
  const handleDescriptionClick = (item: RubricItem) => {
    setEditingItemId(item.id);
    
    // Set textarea value after a brief delay to ensure it's rendered
    setTimeout(() => {
      if (textareaRef.current) {
        const textarea = textareaRef.current;
        textarea.value = item.description || '';
        textarea.focus();
        // Set cursor to end of text
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      }
    }, 0);
  };

  // Handle description save with optimistic update
  const handleSaveDescription = async (itemId: string, description: string) => {
    setIsSaving(itemId);
    
    // Save current state for rollback in case of error
    const previousLikelihoodItems = [...likelihoodRubricItems];
    const previousSeverityItems = [...severityRubricItems];
    
    // Optimistic update: Update Redux state immediately
    const updateItem = (items: RubricItem[]): RubricItem[] => {
      return items.map(item => 
        item.id === itemId ? { ...item, description } : item
      );
    };
    
    dispatch(setLikelihoodRubricItems(updateItem(likelihoodRubricItems)));
    dispatch(setSeverityRubricItems(updateItem(severityRubricItems)));
    
    // Clear editing state immediately for better UX
    setEditingItemId(null);
    
    try {
      // Call API
      await updateRubricItem({
        rubricItemId: itemId,
        rubricItemDescription: description,
      });
      
      // Success: State already updated optimistically, no need to update again
    } catch (error: any) {
      console.error('❌ Failed to save description:', error);
      
      // Rollback: Restore previous state on error
      dispatch(setLikelihoodRubricItems(previousLikelihoodItems));
      dispatch(setSeverityRubricItems(previousSeverityItems));
      
      // Restore editing state so user can try again
      setEditingItemId(itemId);
      
      message.error(error?.message || 'Failed to save description');
    } finally {
      setIsSaving(null);
    }
  };

  // Handle description blur - save text from textarea
  const handleDescriptionBlur = (item: RubricItem) => {
    if (editingItemId === item.id && textareaRef.current) {
      const newDescription = textareaRef.current.value;
      handleSaveDescription(item.id, newDescription);
    }
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Column templates
  const indexTemplate = (props: RubricItem) => {
    return <IndexCell>{props.index.toString().padStart(2, '0')}</IndexCell>;
  };

  const descriptionTemplate = (props: RubricItem) => {
    const isEditing = editingItemId === props.id;
    const isSavingItem = isSaving === props.id;

    if (isEditing) {
      return (
        <EditableDescriptionCell>
          <DescriptionTextarea
            ref={isEditing ? textareaRef : null}
            key={`textarea-${props.id}`}
            defaultValue={props.description || ''}
            onBlur={() => handleDescriptionBlur(props)}
            disabled={isSavingItem}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </EditableDescriptionCell>
      );
    }

    return (
      <EditableDescriptionCell onClick={() => handleDescriptionClick(props)}>
        <DescriptionText>
          {props.description || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Click to edit</span>}
        </DescriptionText>
      </EditableDescriptionCell>
    );
  };

  // Likelihood labels for Visual Table (from Redux data, reverse order)
  const likelihoodLabels = [...likelihoodRubricItems]
    .sort((a, b) => b.index - a.index)
    .map(item => item.name);

  // Impact labels for Visual Table (from Redux data)
  const impactLabels = severityRubricItems.map(item => item.name);

  // Mock risk matrix (still hardcoded as it's not part of rubric items)
  const riskMatrix: ('High' | 'Med' | 'Low')[][] = [
    ['Med', 'Med', 'High', 'High', 'High'],      // Catastrophic
    ['Low', 'Med', 'Med', 'High', 'High'],       // Major
    ['Low', 'Med', 'Med', 'High', 'High'],       // Moderate
    ['Low', 'Low', 'Med', 'Med', 'Med'],         // Minor
    ['Low', 'Low', 'Low', 'Low', 'Med'],         // Insignificant
  ];

  // Show loading state
  if (isLoading) {
    return (
      <TabContent>
        <EmptyStateContainer>
          <LoadingSpinner>
            <div className="spinner" />
            <div className="text">Loading risk matrix configuration...</div>
          </LoadingSpinner>
        </EmptyStateContainer>
      </TabContent>
    );
  }

  // Show error/empty state with reload button
  if (hasError || !isDataReady) {
    return (
      <TabContent>
        <EmptyStateContainer>
          <EmptyStateText>
            {hasError 
              ? 'Failed to load risk matrix configuration' 
              : 'No risk matrix data available'}
          </EmptyStateText>
          <ReloadButton onClick={fetchRubricItems} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Reload'}
          </ReloadButton>
        </EmptyStateContainer>
      </TabContent>
    );
  }

  return (
    <TabContent>
      {/* Section 1: Likelihood Degree Scale */}
      <Section $bgColor="#DBEAFE">
        <SectionHeader onClick={() => toggleSection('likelihood')}>
          <CollapseIcon>
            <DownOutlined style={{ transform: collapsedSections.has('likelihood') ? 'rotate(-90deg)' : 'rotate(0deg)' }} />
          </CollapseIcon>
          <SectionTitle>Likelihood Degree Scale</SectionTitle>
          <SectionActions onClick={(e) => e.stopPropagation()}>
            <ExportButton onClick={() => setExportLikelihoodPanelOpen(true)} />
          </SectionActions>
        </SectionHeader>

        {!collapsedSections.has('likelihood') && (
          <GridWrapper>
            <GridComponent
              key={`likelihood-grid-${likelihoodRubricItems.length}`}
              dataSource={likelihoodRubricItems}
              allowPaging={false}
              enableHover={true}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field='index'
                  headerText='#'
                  width='68'
                  textAlign='Center'
                  template={indexTemplate}
                />
                <ColumnDirective
                  field='name'
                  headerText='Likelihood'
                  width='160'
                  textAlign='Left'
                />
                <ColumnDirective
                  field='description'
                  headerText='Scale degree description'
                  width='auto'
                  textAlign='Left'
                  template={descriptionTemplate}
                />
              </ColumnsDirective>
              <Inject services={[Page]} />
            </GridComponent>
          </GridWrapper>
        )}
      </Section>

      {/* Section 2: Risk Impact Severity Scale */}
      <Section $bgColor="#E0E7FF">
        <SectionHeader onClick={() => toggleSection('impact')}>
          <CollapseIcon>
            <DownOutlined style={{ transform: collapsedSections.has('impact') ? 'rotate(-90deg)' : 'rotate(0deg)' }} />
          </CollapseIcon>
          <SectionTitle>Risk Impact Severity Scale</SectionTitle>
          <SectionActions onClick={(e) => e.stopPropagation()}>
            <ExportButton onClick={() => setExportSeverityPanelOpen(true)} />
          </SectionActions>
        </SectionHeader>

        {!collapsedSections.has('impact') && (
          <GridWrapper>
            <GridComponent
              key={`severity-grid-${severityRubricItems.length}`}
              dataSource={severityRubricItems}
              allowPaging={false}
              enableHover={true}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field='index'
                  headerText='#'
                  width='68'
                  textAlign='Center'
                  template={indexTemplate}
                />
                <ColumnDirective
                  field='name'
                  headerText='Impact Severity'
                  width='160'
                  textAlign='Left'
                />
                <ColumnDirective
                  field='description'
                  headerText='Scale degree description'
                  width='auto'
                  textAlign='Left'
                  template={descriptionTemplate}
                />
              </ColumnsDirective>
              <Inject services={[Page]} />
            </GridComponent>
          </GridWrapper>
        )}
      </Section>

      {/* Section 3: Visual Table (Risk Matrix) */}
      <VisualTableSection>
        <SectionHeader onClick={() => toggleSection('visual')}>
          <CollapseIcon>
            <DownOutlined style={{ transform: collapsedSections.has('visual') ? 'rotate(-90deg)' : 'rotate(0deg)' }} />
          </CollapseIcon>
          <SectionTitle>Visual Table</SectionTitle>
        </SectionHeader>

        {!collapsedSections.has('visual') && (
          <RiskMatrixContainer>
            <RiskMatrixTable
              likelihoodLabels={likelihoodLabels}
              impactLabels={impactLabels}
              riskMatrix={riskMatrix}
              interactive={false}
              showTitle={true}
            />
          </RiskMatrixContainer>
        )}
      </VisualTableSection>

      {/* Export Panel - Likelihood */}
      <ExportPanel
        open={exportLikelihoodPanelOpen}
        onClose={() => setExportLikelihoodPanelOpen(false)}
        data={likelihoodExportData}
        columns={exportColumns}
        fileName="LikelihoodDegreeScale"
        title="Likelihood Degree Scale"
      />

      {/* Export Panel - Severity */}
      <ExportPanel
        open={exportSeverityPanelOpen}
        onClose={() => setExportSeverityPanelOpen(false)}
        data={severityExportData}
        columns={exportColumns}
        fileName="RiskImpactSeverityScale"
        title="Risk Impact Severity Scale"
      />
    </TabContent>
  );
};

export default Tab4RiskMatrixConfiguration;

